"use client";

import { useState } from "react";
import { type EnrichedTweet, getMediaUrl, getMp4Video } from "react-tweet";
import type { MediaAnimatedGif, MediaVideo } from "react-tweet/api";

// Same hosts as in inline-video.tsx
const TWITTER_VIDEO_HOSTS = [
  "video.twimg.com",
  "pbs.twimg.com",
  "abs.twimg.com",
  "amp.twimg.com",
];

function isTwitterVideoUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return TWITTER_VIDEO_HOSTS.includes(parsed.hostname);
  } catch {
    return false;
  }
}

function getProxiedVideoUrl(src: string): string {
  if (!isTwitterVideoUrl(src)) {
    return src;
  }
  return `/api/video-proxy?url=${encodeURIComponent(src)}`;
}

type Props = {
  tweet: EnrichedTweet;
  media: MediaAnimatedGif | MediaVideo;
};

/**
 * Custom tweet video component that proxies Twitter video URLs
 * through our video-proxy API to avoid CORS issues.
 */
export function CustomTweetMediaVideo({ tweet, media }: Props) {
  const [playButton, setPlayButton] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [ended, setEnded] = useState(false);

  const mp4Video = getMp4Video(media);
  const proxiedUrl = getProxiedVideoUrl(mp4Video.url);
  const posterUrl = getMediaUrl(media, "small");

  let timeout: number | null = null;

  return (
    <>
      {/* biome-ignore lint/a11y/useMediaCaption: Twitter videos don't provide caption tracks */}
      <video
        className="relative w-full rounded-xl"
        controls={!playButton}
        onEnded={() => {
          setEnded(true);
        }}
        onPause={() => {
          if (timeout !== null) {
            window.clearTimeout(timeout);
          }
          timeout = window.setTimeout(() => {
            if (isPlaying) {
              setIsPlaying(false);
            }
            timeout = null;
          }, 100);
        }}
        onPlay={() => {
          if (timeout !== null) {
            window.clearTimeout(timeout);
          }
          if (!isPlaying) {
            setIsPlaying(true);
          }
          if (ended) {
            setEnded(false);
          }
        }}
        playsInline
        poster={posterUrl}
        preload="none"
        tabIndex={playButton ? -1 : 0}
      >
        <source src={proxiedUrl} type={mp4Video.content_type} />
      </video>

      {playButton && (
        <button
          aria-label="Play video"
          className="absolute inset-0 flex items-center justify-center bg-black/20 transition-colors hover:bg-black/30"
          onClick={(e) => {
            const video = e.currentTarget.previousSibling as HTMLVideoElement;
            e.preventDefault();
            setPlayButton(false);
            video.load();
            video
              .play()
              .then(() => {
                setIsPlaying(true);
                video.focus();
              })
              .catch((_error) => {
                setPlayButton(true);
                setIsPlaying(false);
              });
          }}
          type="button"
        >
          <svg
            aria-hidden="true"
            className="h-16 w-16 text-white drop-shadow-lg"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M21 12L4 2v20l17-10z" />
          </svg>
        </button>
      )}

      {!(isPlaying || ended) && (
        <div className="absolute bottom-2 left-2">
          <a
            className="rounded bg-black/60 px-2 py-1 text-white text-xs hover:bg-black/80"
            href={tweet.url}
            rel="noopener noreferrer"
            target="_blank"
          >
            {playButton ? "Watch on X" : "Continue watching on X"}
          </a>
        </div>
      )}

      {ended && (
        <a
          className="absolute inset-0 flex items-center justify-center bg-black/40 text-white hover:bg-black/50"
          href={tweet.url}
          rel="noopener noreferrer"
          target="_blank"
        >
          View replies
        </a>
      )}
    </>
  );
}
